import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { Table } from "@tanstack/react-table";
import { MobileBottomSheet } from "components/general/MobileBottomSheet/MobileBottomSheet";
import { SkeletonBlock } from "components/general/Skeleton";
import { MAP_FILTER_TO_COL_ID } from "hooks/useActivityColumnDefinitions";
import { useIsMobile } from "hooks/useDeviceType";
import { useDisplayOnClick } from "hooks/useDisplayOnClick";
import { useIsTradeHistoryReturned } from "hooks/useIsTradeHistoryReturned";
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TransactionDetail } from "types/history";
import { withIsVisible } from "utils/reactUtils";
import { Filter, FiltersInterface } from "../../config/consts";
import { FilterButton } from "./elements/FilterButton";
import { FilterButtonContent } from "./elements/FilterButtonContent";
import { FilterDropdownClearButton } from "./elements/FilterDropdownClearButton";
import { FilterDropdownDoneButton } from "./elements/FilterDropdownDoneButton";

import { css } from "@emotion/react";
import { useRect } from "hooks/useRect";
import { deepEqual } from "wagmi";
import { GlobalStore } from "../../store/global/GlobalStore";
import { Theme } from "../../styles/theme";
import AnimatedDropdownContent from "../AnimatedDropdown/AnimatedDropdown";

interface FilterDropdownButtonProps<T extends FiltersInterface[keyof FiltersInterface]> {
  children: ReactNode;
  filterType: Filter;
  localOptions: T | undefined;
  setLocalOptions: React.Dispatch<React.SetStateAction<T | undefined>>;
  confirmedOptions: T | undefined;
  setConfirmedOptions: (updatedOptions: T | undefined) => void;
  additionalOnClear?: () => void;
  additionalOnDone?: () => void;
  preventFiltering?: boolean;
  table: Table<TransactionDetail>;
  onShown?: () => void;
  onHidden?: () => void;
}

export const FilterDropdownButton = <T extends FiltersInterface[keyof FiltersInterface]>({
  children,
  filterType,
  localOptions,
  confirmedOptions,
  setLocalOptions,
  setConfirmedOptions,
  additionalOnClear,
  additionalOnDone,
  preventFiltering = false,
  table,
  onShown,
  onHidden,
}: FilterDropdownButtonProps<T>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const theme = GlobalStore.useState(s => s.theme);
  const [hasFiredShownHandler, setHasFiredShownHandler] = useState(false);
  const {
    isDisplayed,
    animationState,
    toggleDisplay,
    elementToClickRef,
    elementToDisplayRef,
    forceClose: forceCloseFilter,
  } = useDisplayOnClick({
    onExit: () => {
      // Clear the token filter search input
      if (additionalOnClear != null) {
        additionalOnClear();
      }
      // Check options deeply to avoid unnecessary re-renders)
      if (deepEqual(localOptions, confirmedOptions)) return;
      setLocalOptions(confirmedOptions);
    },
    duration: 250,
  });

  useEffect(() => {
    if (isDisplayed && !hasFiredShownHandler) {
      onShown?.();
      setHasFiredShownHandler(true);
    } else if (!isDisplayed && hasFiredShownHandler) {
      onHidden?.();
      setHasFiredShownHandler(false);
    }
  }, [hasFiredShownHandler, isDisplayed, onHidden, onShown]);

  const isMobile = useIsMobile();
  const isTradeHistoryReturned = useIsTradeHistoryReturned();
  const colId = MAP_FILTER_TO_COL_ID[filterType];
  const column = table.getColumn(colId);

  const onClear = useCallback(() => {
    column?.setFilterValue(undefined);
    setLocalOptions(undefined);
    setConfirmedOptions(undefined);
    if (additionalOnClear != null) {
      additionalOnClear();
    }
  }, [column, setConfirmedOptions, setLocalOptions, additionalOnClear]);

  const onDone = useCallback(() => {
    if (!preventFiltering) {
      column?.setFilterValue(localOptions);
      setConfirmedOptions(localOptions);
    }
    if (additionalOnDone != null) {
      additionalOnDone();
    }
  }, [additionalOnDone, column, localOptions, preventFiltering, setConfirmedOptions]);

  const dropdownContent = useCallback(() => {
    return (
      <>
        {children}
        <ClearAndDoneButtonContainer>
          <FilterDropdownClearButton
            onClick={() => {
              onClear();
              forceCloseFilter();
            }}
            disabled={localOptions == null && confirmedOptions == null}
          >
            Clear
          </FilterDropdownClearButton>
          <FilterDropdownDoneButton
            onClick={() => {
              onDone();
              if (!preventFiltering) {
                forceCloseFilter();
              }
            }}
            disabled={localOptions == null && confirmedOptions == null}
          >
            Done
          </FilterDropdownDoneButton>
        </ClearAndDoneButtonContainer>
      </>
    );
  }, [
    children,
    confirmedOptions,
    forceCloseFilter,
    localOptions,
    onClear,
    onDone,
    preventFiltering,
  ]);

  // Ensure dropdown content top and left values are updated on page resize
  const elementToClickRect = useRect(elementToClickRef);
  const dropdownCss = useMemo(() => {
    if (elementToClickRect == null) {
      return filterDropdownButtonContent(theme, -100000, -10000);
    }
    return filterDropdownButtonContent(
      theme,
      elementToClickRect.left ?? 0,
      elementToClickRect.top ?? 0
    );
  }, [elementToClickRect, theme]);

  return (
    <div ref={containerRef}>
      <Container isVisible={isTradeHistoryReturned}>
        <FilterButton
          onClick={toggleDisplay}
          hasFilterOptionsSelected={localOptions != null}
          showingDropdown={isDisplayed}
          ref={elementToClickRef as React.Ref<HTMLButtonElement> | undefined}
        >
          <FilterButtonContent
            filterType={filterType}
            localOptions={localOptions}
            confirmedOptions={confirmedOptions}
          />
        </FilterButton>

        {/* Dropdown content shown after user selects filter */}
        {isDisplayed &&
          (isMobile ? (
            <MobileBottomSheet isOpen={isDisplayed} setIsOpen={forceCloseFilter}>
              {dropdownContent()}
            </MobileBottomSheet>
          ) : (
            <Overlay isOpen={isDisplayed} aria-label={"Filter Dropdown Overlay"}>
              <AnimatedDropdownContent
                childContainerProps={{ css: dropdownCss }}
                openingAnimation={openingFilterAnimation}
                closingAnimation={closingFilterAnimation}
                ref={elementToDisplayRef as React.Ref<HTMLDivElement> | undefined}
                animationState={animationState}
                duration={250}
                aria-modal
              >
                {dropdownContent()}
              </AnimatedDropdownContent>
            </Overlay>
          ))}
      </Container>
      {isTradeHistoryReturned ? null : (
        <SkeletonBlock
          isVisible={!isTradeHistoryReturned}
          width={isMobile ? 100 : 110}
          height={32}
          borderRadius={200}
          wrapper={SkeletonContainer}
        />
      )}
    </div>
  );
};

// Sub-components
const Overlay = styled.div<{ isOpen: boolean }>(props => {
  return {
    display: props.isOpen ? "block" : "none",
  };
});

const Container = withIsVisible(styled.div(() => ({ display: "inline-block" })));

const ClearAndDoneButtonContainer = styled.div`
  padding-top: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const SkeletonContainer = styled.div(() => {
  return { marginRight: "10px", height: "32px" };
});

export const openingFilterAnimation = keyframes`
  0% {
    transform: translateY(-20px);
    opacity: 0.5;
    max-height: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
    max-height: 470px;
  }
`;

export const closingFilterAnimation = keyframes`
  0% {
    transform: translateY(0);
    opacity: 1;
    max-height: 470px;
  }
  100% {
    transform: translateY(-20px);
    opacity: 0.5;
    max-height: 0;
  }
`;

// Animation

export const filterDropdownButtonContent = (theme: Theme, left: number, top: number) => css`
  position: absolute;
  background: ${theme.surface.alt.secondary.default};
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.25);
  padding: 8px;
  left: ${left}px;
  border-radius: 20px;
  color: ${theme.font.primary.default};
  max-width: 276px;
  width: 276px;
  z-index: 20;
  overflow: hidden;
`;
